import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { ArrowLeft, Upload, FileText, CheckCircle, Cloud, Lock, Loader2, ExternalLink, X } from 'lucide-react';
import type { User } from '../App';
import { categories, stages } from '../lib/mockData';
import { createSubmission, uploadDocuments, finalizeSubmission } from '../api/innovations';
import { getCurrentUser } from '../api/auth';
import { redeemInnovatorVoucher, redeemAssessmentDiscountVoucher } from '../api/billing';

const PAYSTACK_INNOVATOR_URL =
  import.meta.env.VITE_PAYSTACK_INNOVATOR_PAGE_URL || 'https://paystack.shop/pay/insoliptechnologies';

function innovatorPaystackUrl (email: string): string {
  try {
    const u = new URL(PAYSTACK_INNOVATOR_URL);
    u.searchParams.set('email', email.trim());
    return u.toString();
  } catch {
    return `${PAYSTACK_INNOVATOR_URL}${PAYSTACK_INNOVATOR_URL.includes('?') ? '&' : '?'}email=${encodeURIComponent(email.trim())}`;
  }
}

interface SubmitInnovationProps {
  user: User;
  onBack: () => void;
}

const bypassPayments = import.meta.env.VITE_BYPASS_PAYMENTS === 'true';

interface InnovatorPackage {
  id: 'spark' | 'blueprint' | 'shield';
  tier: string;
  name: string;
  tagline: string;
  priceKes: number;
  highlight?: string;
  bullets: string[];
}

const INNOVATOR_PACKAGES: InnovatorPackage[] = [
  {
    id: 'spark',
    tier: 'Starter',
    name: 'IP Spark',
    tagline: 'Know your IP potential',
    priceKes: 10000,
    bullets: [
      'Expert assessment of your innovation by IP professionals',
      'Detailed IP potential report covering patent, trademark, copyright, and trade secret viability',
      'Strategic recommendations on the best IP protection path',
      "Peace of mind before investing further in development or filing"
    ]
  },
  {
    id: 'blueprint',
    tier: 'Growth',
    name: 'IP Blueprint',
    tagline: 'Assess, strategise & prepare',
    priceKes: 50000,
    highlight: 'Most popular',
    bullets: [
      'Everything in IP Spark, plus',
      'Full preparation of IP filing documentation by accredited experts',
      'KIPI-compliant application package prepared for filing',
      'Filing-ready dossier so you can file directly or upgrade to full support'
    ]
  },
  {
    id: 'shield',
    tier: 'Complete',
    name: 'IP Shield',
    tagline: 'Full protection, end to end',
    priceKes: 60000,
    bullets: [
      'Everything in IP Blueprint, plus',
      'Insolip files your IP application directly with KIPI on your behalf',
      'Dedicated follow-up with KIPI through each stage',
      'Real-time status updates until certificate issuance'
    ]
  }
];

export function SubmitInnovation({ user, onBack }: SubmitInnovationProps) {
  const needsPaymentGate = user.role === 'innovator' && !bypassPayments;
  const [paymentGateReady, setPaymentGateReady] = useState(!needsPaymentGate);
  const [currentStep, setCurrentStep] = useState<'packages' | 'form' | 'payment' | 'success'>('form');
  const [selectedPackageId, setSelectedPackageId] = useState<InnovatorPackage['id']>('blueprint');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCloudModal, setShowCloudModal] = useState(false);
  const [selectedCloud, setSelectedCloud] = useState<string | null>(null);
  const [bulkImportCount, setBulkImportCount] = useState('');
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherBusy, setVoucherBusy] = useState(false);
  const [voucherError, setVoucherError] = useState<string | null>(null);
  const [discountCode, setDiscountCode] = useState('');
  const [discountBusy, setDiscountBusy] = useState(false);
  const [discountError, setDiscountError] = useState<string | null>(null);
  
  const MAX_DOCS = 10;

  const [formData, setFormData] = useState({
    title: '',
    category: '',
    stage: '',
    keywords: '',
    description: '',
    ipUniqueness: '',
    targetUsers: '',
    files: [] as File[]
  });

  const selectedPackage = INNOVATOR_PACKAGES.find((pkg) => pkg.id === selectedPackageId) || INNOVATOR_PACKAGES[1];
  const ASSESSMENT_FEE = selectedPackage.priceKes;

  useEffect(() => {
    if (!needsPaymentGate) return;
    let cancelled = false;
    (async () => {
      try {
        const { user: u } = await getCurrentUser();
        if (cancelled) return;
        setCurrentStep(u.innovator_assessment_fee_paid_at ? 'form' : 'packages');
      } catch {
        if (!cancelled) setCurrentStep('packages');
      } finally {
        if (!cancelled) setPaymentGateReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [needsPaymentGate]);

  useEffect(() => {
    if (currentStep !== 'payment' || bypassPayments) return;
    const id = setInterval(async () => {
      try {
        const { user: u } = await getCurrentUser();
        if (u.innovator_assessment_fee_paid_at) {
          setCurrentStep('form');
        }
      } catch {
        /* ignore */
      }
    }, 4000);
    return () => clearInterval(id);
  }, [currentStep, bypassPayments]);

  const cloudProviders = [
    { id: 'gdrive', name: 'Google Drive', icon: '☁️', color: 'bg-yellow-100 text-yellow-700' },
    { id: 'onedrive', name: 'OneDrive', icon: '📁', color: 'bg-blue-100 text-blue-700' },
    { id: 'terabox', name: 'TeraBox', icon: '📦', color: 'bg-purple-100 text-purple-700' }
  ];

  // Map frontend stage to backend current_state
  const mapStageToCurrentState = (stage: string): 'idea' | 'exploration' | 'concept' | 'prototype' | 'demo' => {
    const stageMap: Record<string, 'idea' | 'exploration' | 'concept' | 'prototype' | 'demo'> = {
      'prototype': 'prototype',
      'pilot': 'demo',
      'market-ready': 'demo',
      'idea': 'idea',
      'exploration': 'exploration',
      'concept': 'concept',
      'demo': 'demo'
    };
    return stageMap[stage.toLowerCase()] || 'idea';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      // Client-side validation (matches backend rules)
      const title = formData.title.trim();
      const abstract = formData.description.trim();
      if (title.length < 10 || title.length > 200) {
        setError('Title must be between 10 and 200 characters.');
        setSubmitting(false);
        return;
      }
      if (abstract.length < 50 || abstract.length > 3000) {
        setError('Description must be between 50 and 3000 characters.');
        setSubmitting(false);
        return;
      }

      const ipU = formData.ipUniqueness.trim();
      if (ipU.length < 20 || ipU.length > 2000) {
        setError('IP uniqueness must be between 20 and 2000 characters.');
        setSubmitting(false);
        return;
      }

      // Map form data to API format
      const keywordsArray = formData.keywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0);

      // Create submission (draft)
      const submissionData = {
        title,
        abstract,
        current_state: mapStageToCurrentState(formData.stage),
        keywords: keywordsArray,
        technology_area: formData.category?.trim() || undefined,
        expected_outcome: formData.targetUsers?.trim() || undefined,
        ip_uniqueness: ipU
      };

      const submission = await createSubmission(submissionData);

      if (formData.files.length > 0) {
        try {
          await uploadDocuments(submission.submission_uuid, formData.files);
        } catch (uploadError: any) {
          console.error('File upload error:', uploadError);
          const res = uploadError.response?.data;
          const msg = res?.message || res?.error || uploadError.message || 'Unknown error';
          setError(`Submission saved as draft. Document upload failed: ${msg}. You can reopen the draft and try uploading again.`);
          setSubmitting(false);
          return;
        }
      }

      // Finalize submission (draft → submitted)
      await finalizeSubmission(submission.submission_uuid);

      setSubmitted(true);
      setCurrentStep('success');
      setTimeout(() => {
        onBack();
      }, 3000);
    } catch (err: any) {
      console.error('Submission error:', err);
      if (err.response?.status === 402) {
        setError(
          'The innovator assessment fee is required before submitting. Complete payment on Paystack, then try again.'
        );
        setSubmitting(false);
        if (user.role === 'innovator' && !bypassPayments) setCurrentStep('payment');
        return;
      }
      const res = err.response?.data;
      let message = err.message || 'Failed to submit innovation. Please try again.';
      if (res?.details && Array.isArray(res.details) && res.details.length > 0) {
        message = res.details.map((d: { msg?: string }) => d.msg).filter(Boolean).join(' ');
      } else if (res?.message && typeof res.message === 'string') {
        message = res.message;
      } else if (res?.error && typeof res.error === 'string') {
        message = res.error;
      }
      setError(message);
      setSubmitting(false);
    }
  };

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list?.length) return;
    const next = [...formData.files];
    for (let i = 0; i < list.length && next.length < MAX_DOCS; i++) {
      next.push(list[i]);
    }
    setFormData({ ...formData, files: next });
    e.target.value = '';
  };

  const removeFileAt = (index: number) => {
    setFormData({
      ...formData,
      files: formData.files.filter((_, i) => i !== index)
    });
  };

  const handleCloudConnect = (provider: string) => {
    setSelectedCloud(provider);
  };

  const handleBulkImport = () => {
    const count = parseInt(bulkImportCount);
    if (count > 0) {
      alert(`Successfully imported ${count} innovation documents from ${cloudProviders.find(p => p.id === selectedCloud)?.name}. They will be queued for assessment.`);
      setShowCloudModal(false);
      setSelectedCloud(null);
      setBulkImportCount('');
      setTimeout(() => {
        onBack();
      }, 1500);
    }
  };

  if (needsPaymentGate && !paymentGateReady) {
    return (
      <div className="max-w-4xl mx-auto p-6 flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <Loader2 className="w-10 h-10 animate-spin text-[var(--royal)]" />
        <p className="text-slate-600 text-sm">Checking payment status…</p>
      </div>
    );
  }

  const handleRedeemVoucher = async (e: React.FormEvent) => {
    e.preventDefault();
    setVoucherError(null);
    const code = voucherCode.trim();
    if (code.length < 4) {
      setVoucherError('Enter a voucher code (at least 4 characters).');
      return;
    }
    setVoucherBusy(true);
    try {
      await redeemInnovatorVoucher(code);
      const { user: u } = await getCurrentUser();
      if (u.innovator_assessment_fee_paid_at) {
        setCurrentStep('form');
      }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      setVoucherError(ax.response?.data?.message || ax.message || 'Could not redeem voucher.');
    } finally {
      setVoucherBusy(false);
    }
  };

  const handleRedeemAssessmentDiscount = async (e: React.FormEvent) => {
    e.preventDefault();
    setDiscountError(null);
    const code = discountCode.trim();
    if (code.length < 4) {
      setDiscountError('Enter a discount voucher code (at least 4 characters).');
      return;
    }
    setDiscountBusy(true);
    try {
      const res = await redeemAssessmentDiscountVoucher(code);
      if (!res.payment_page_url) {
        setDiscountError('No discounted checkout URL was returned.');
        return;
      }
      window.open(res.payment_page_url, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      setDiscountError(ax.response?.data?.message || ax.message || 'Could not apply discount voucher.');
    } finally {
      setDiscountBusy(false);
    }
  };

  const handlePackageContinue = () => {
    setCurrentStep('payment');
  };

  if (currentStep === 'packages' && user.role === 'innovator') {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <Button variant="ghost" onClick={onBack} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-8">
          <h1 className="text-slate-900 mb-2">Choose your IP package</h1>
          <p className="text-slate-600">
            Select a package, then proceed to payment. After payment is confirmed, you continue with submission as before.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
          {INNOVATOR_PACKAGES.map((pkg) => {
            const isSelected = selectedPackageId === pkg.id;
            return (
              <Card
                key={pkg.id}
                className={`relative h-full flex flex-col ${isSelected ? 'border-2 border-[var(--royal)] shadow-lg' : 'border border-slate-200'}`}
              >
                {pkg.highlight && (
                  <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[var(--royal)] text-white">
                    {pkg.highlight}
                  </Badge>
                )}
                <CardHeader>
                  <div className="text-xs uppercase tracking-wide text-slate-500">{pkg.tier}</div>
                  <CardTitle>{pkg.name}</CardTitle>
                  <CardDescription>{pkg.tagline}</CardDescription>
                  <div className="pt-2 text-2xl font-semibold text-slate-900">KES {pkg.priceKes.toLocaleString()}</div>
                </CardHeader>
                <CardContent className="flex flex-col gap-3 flex-1">
                  {pkg.bullets.map((item) => (
                    <div key={item} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-[var(--royal)] flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                  <Button
                    type="button"
                    className="mt-auto"
                    variant={isSelected ? 'default' : 'outline'}
                    onClick={() => setSelectedPackageId(pkg.id)}
                  >
                    {isSelected ? 'Selected' : 'Select package'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Alert className="mb-6 bg-slate-50 border-slate-200">
          <AlertDescription className="text-slate-700 text-sm">
            All packages include a dedicated IP expert. KIPI filing fees are billed separately.
          </AlertDescription>
        </Alert>

        <div className="flex gap-3">
          <Button type="button" variant="outline" className="flex-1" onClick={onBack}>
            Cancel
          </Button>
          <Button type="button" className="flex-1" onClick={handlePackageContinue}>
            Continue with {selectedPackage.name} (KES {selectedPackage.priceKes.toLocaleString()})
          </Button>
        </div>
      </div>
    );
  }

  // Payment step — Paystack (charge.success webhook unlocks submission APIs)
  if (currentStep === 'payment' && user.role === 'innovator') {
    const payUrl = innovatorPaystackUrl(user.email);
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Button variant="ghost" onClick={() => setCurrentStep('packages')} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Packages
        </Button>

        <div className="mb-6">
          <h1 className="text-slate-900 mb-2">Package payment</h1>
          <p className="text-slate-600">
            Complete payment for <strong>{selectedPackage.name}</strong> on Paystack to unlock submission. Use the{' '}
            <strong>same email</strong> as your Insolip account ({user.email}) so we can activate access automatically.
          </p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Selected package</CardTitle>
            <CardDescription>Secured by Paystack checkout</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-[var(--gold-pale)] rounded-lg border border-[rgba(201,168,76,0.2)]">
              <div>
                <div className="text-slate-600 mb-1">{selectedPackage.name}</div>
                <div className="text-slate-900">{selectedPackage.tagline} — KES {ASSESSMENT_FEE.toLocaleString()}</div>
              </div>
              <Button asChild className="shrink-0">
                <a href={payUrl} target="_blank" rel="noopener noreferrer">
                  Open Paystack <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </Button>
            </div>

            <div className="rounded-lg border border-[rgba(201,168,76,0.35)] bg-white/80 p-4 space-y-3">
              <p className="text-sm font-medium text-slate-900">Have a voucher code?</p>
              <p className="text-sm text-slate-600">
                Promotional or test codes unlock the same submission access without Paystack. Each code can only be used once per account.
              </p>
              <form onSubmit={handleRedeemVoucher} className="flex flex-col sm:flex-row gap-2 sm:items-end">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="voucher-code" className="text-slate-700">
                    Voucher code
                  </Label>
                  <Input
                    id="voucher-code"
                    type="text"
                    autoComplete="off"
                    placeholder="e.g. INSOLIP-TEST-2026"
                    value={voucherCode}
                    onChange={(e) => setVoucherCode(e.target.value)}
                    disabled={voucherBusy}
                    className="uppercase"
                  />
                </div>
                <Button type="submit" variant="secondary" disabled={voucherBusy} className="shrink-0">
                  {voucherBusy ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Applying…
                    </>
                  ) : (
                    'Apply voucher'
                  )}
                </Button>
              </form>
              {voucherError && (
                <p className="text-sm text-red-600" role="alert">
                  {voucherError}
                </p>
              )}
            </div>

            <div className="rounded-lg border border-sky-300 bg-sky-50/70 p-4 space-y-3">
              <p className="text-sm font-medium text-slate-900">Have a 40% discount voucher?</p>
              <p className="text-sm text-slate-700">
                Discount vouchers reduce the assessment payment to <strong>KES 6,000</strong> (from KES {ASSESSMENT_FEE.toLocaleString()}).
                Apply the code, then complete payment on the discounted Paystack page that opens.
              </p>
              <form onSubmit={handleRedeemAssessmentDiscount} className="flex flex-col sm:flex-row gap-2 sm:items-end">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="assessment-discount-code" className="text-slate-700">
                    Discount voucher code
                  </Label>
                  <Input
                    id="assessment-discount-code"
                    type="text"
                    autoComplete="off"
                    placeholder="e.g. INSOLIP-DISCOUNT-40"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value)}
                    disabled={discountBusy}
                    className="uppercase"
                  />
                </div>
                <Button type="submit" disabled={discountBusy} className="shrink-0">
                  {discountBusy ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Opening…
                    </>
                  ) : (
                    'Apply 40% voucher'
                  )}
                </Button>
              </form>
              {discountError && (
                <p className="text-sm text-red-600" role="alert">
                  {discountError}
                </p>
              )}
            </div>

            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                After you pay, this page checks every few seconds for confirmation. You can return here from Paystack or
                keep this tab open — no need to refresh.
              </AlertDescription>
            </Alert>
            <Alert className="bg-slate-50 border-slate-200">
              <Lock className="h-4 w-4 text-slate-600" />
              <AlertDescription className="text-slate-700">
                If payment used a different email than your account, we cannot match it. Contact support with your Paystack
                receipt reference.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success Screen
  if (currentStep === 'success') {
    return (
      <div className="max-w-2xl mx-auto p-6 min-h-screen flex items-center justify-center">
        <Card className="w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-slate-900 mb-2">Submission Successful!</h2>
            <p className="text-slate-600 mb-4">
              Your innovation has been submitted for Intellectual Property assessment. You will be notified when an Independent Reviewer evaluates your submission.
            </p>
            <Button onClick={onBack}>
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Form Step
  return (
    <div className="max-w-4xl mx-auto p-6">
      <Button variant="ghost" onClick={onBack} className="mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Dashboard
      </Button>

      <div className="mb-6">
        <h1 className="text-slate-900 mb-2">Submit Innovation</h1>
        <p className="text-slate-600">
          Provide details about your innovation for Intellectual Property protection assessment
        </p>
      </div>

      {user.role === 'innovator' && !bypassPayments && (
        <Alert className="mb-6 bg-[var(--gold-pale)] border-[rgba(201,168,76,0.3)]">
          <CheckCircle className="h-4 w-4 text-[var(--royal)]" />
          <AlertDescription className="text-[var(--navy)]">
            <strong>Payment Confirmed:</strong> {selectedPackage.name} payment of KES {ASSESSMENT_FEE.toLocaleString()} has been received.
          </AlertDescription>
        </Alert>
      )}
      {user.role === 'innovator' && bypassPayments && (
        <Alert className="mb-6 bg-amber-50 border-amber-200">
          <AlertDescription className="text-amber-800">
            <strong>Testing:</strong> Payment step is bypassed (VITE_BYPASS_PAYMENTS=true). You can submit the form directly.
          </AlertDescription>
        </Alert>
      )}

      {user.role === 'institution' && (
        <Alert className="mb-6 bg-[var(--gold-pale)] border-[rgba(201,168,76,0.3)]">
          <Cloud className="h-4 w-4 text-[var(--gold)]" />
          <AlertDescription className="text-[var(--navy)]">
            <strong>Bulk Submission Available:</strong> As an institution, you can connect your cloud repository to import multiple innovation documents at once.
            <Button
              variant="link"
              className="text-[var(--gold)] p-0 ml-2 h-auto"
              onClick={() => setShowCloudModal(true)}
            >
              Connect Cloud Repository
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert className="bg-red-50 border-red-200 mb-4">
          <AlertDescription className="text-red-800">
            {error}
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Innovation Details</CardTitle>
            <CardDescription>
              Provide comprehensive information about your innovation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Innovation Title *</Label>
              <Input
                id="title"
                required
                minLength={10}
                maxLength={200}
                placeholder="Enter a descriptive title for your innovation"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
              <p className="text-slate-500 text-sm">Between 10 and 200 characters</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger id="category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="stage">Development Stage *</Label>
                <Select value={formData.stage} onValueChange={(value) => setFormData({ ...formData, stage: value })}>
                  <SelectTrigger id="stage">
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {stages.map(stage => (
                      <SelectItem key={stage} value={stage}>
                        {stage.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="keywords">Keywords *</Label>
              <Input
                id="keywords"
                required
                placeholder="e.g., AI, Medical Imaging, Diagnostics"
                value={formData.keywords}
                onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
              />
              <p className="text-slate-500 text-sm">Separate keywords with commas</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Innovation Description *</Label>
              <div className="rounded-md border border-slate-200 bg-slate-50/80 p-3 text-sm text-slate-700 space-y-2 mb-1">
                <p className="font-medium text-slate-800">What to include</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    <strong>How it works</strong> — the flow, logic, or mechanism; main{' '}
                    <strong>components</strong> (hardware, software, materials) that make it function.
                  </li>
                  <li>
                    <strong>For products</strong> — describe the <strong>process</strong> (how it is made or
                    delivered) and the <strong>raw materials or inputs</strong> combined to produce the product.
                  </li>
                  <li>Technical context, integrations, and dependencies where relevant.</li>
                </ul>
              </div>
              <Textarea
                id="description"
                required
                minLength={50}
                maxLength={3000}
                rows={8}
                placeholder="Explain how your innovation works, its components, and (for products) the process and materials involved…"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
              <p className="text-slate-500 text-sm">
                Between 50 and 3000 characters (submitted as the abstract)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ipUniqueness">Uniqueness of the IP *</Label>
              <p className="text-slate-600 text-sm">
                Describe what makes your intellectual property <strong>novel or distinct</strong> compared to
                existing solutions — e.g. new method, composition, use, or technical effect.
              </p>
              <Textarea
                id="ipUniqueness"
                required
                minLength={20}
                maxLength={2000}
                rows={5}
                placeholder="What is new, non-obvious, or competitively different about your IP?"
                value={formData.ipUniqueness}
                onChange={(e) => setFormData({ ...formData, ipUniqueness: e.target.value })}
              />
              <p className="text-slate-500 text-sm">Between 20 and 2000 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="targetUsers">Target Users / Impact Area *</Label>
              <Textarea
                id="targetUsers"
                required
                rows={3}
                placeholder="Describe the target users, beneficiaries, and expected impact of your innovation"
                value={formData.targetUsers}
                onChange={(e) => setFormData({ ...formData, targetUsers: e.target.value })}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Supporting documents</CardTitle>
            <CardDescription>
              Upload one or more files (PDF, Word). You can add up to {MAX_DOCS} documents per submission.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-[var(--gold)] transition-colors">
              <input
                type="file"
                id="file-upload"
                className="hidden"
                accept=".pdf,.doc,.docx"
                multiple
                onChange={handleFilesChange}
                disabled={formData.files.length >= MAX_DOCS}
              />
              <label
                htmlFor="file-upload"
                className={`cursor-pointer ${formData.files.length >= MAX_DOCS ? 'opacity-50 pointer-events-none' : ''}`}
              >
                <Upload className="w-10 h-10 mx-auto mb-3 text-slate-400" />
                <div className="text-slate-900 mb-1">Add documents</div>
                <p className="text-slate-500 text-sm">
                  PDF, DOC, or DOCX — select multiple files (max {MAX_DOCS} total)
                </p>
              </label>
            </div>

            {formData.files.length > 0 && (
              <ul className="space-y-2">
                {formData.files.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-[var(--gold)] shrink-0" />
                      <span className="truncate text-slate-800" title={f.name}>
                        {f.name}
                      </span>
                      <span className="text-slate-400 shrink-0">({(f.size / 1024).toFixed(0)} KB)</span>
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="shrink-0 h-8 w-8 p-0"
                      onClick={() => removeFileAt(i)}
                      aria-label={`Remove ${f.name}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            <Alert className="bg-amber-50 border-amber-200">
              <Lock className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-900">
                <strong>Document Security:</strong> All documents are stored securely with read-only access and watermarking. Downloads and offline sharing are disabled to protect your Intellectual Property.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Button type="button" variant="outline" onClick={onBack} className="flex-1">
            Cancel
          </Button>
          <Button type="submit" className="flex-1" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting…
              </>
            ) : (
              'Submit for assessment'
            )}
          </Button>
        </div>
      </form>

      {/* Cloud Repository Modal */}
      {showCloudModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-2xl w-full">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Connect Cloud Repository</CardTitle>
                  <CardDescription>Import innovation documents in bulk from your cloud storage</CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowCloudModal(false);
                    setSelectedCloud(null);
                    setBulkImportCount('');
                  }}
                >
                  ✕
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="pt-6 space-y-6">
              {!selectedCloud ? (
                <>
                  <p className="text-slate-600">
                    Select your cloud storage provider to connect and import multiple innovation documents
                  </p>
                  
                  <div className="space-y-3">
                    {cloudProviders.map((provider) => (
                      <button
                        key={provider.id}
                        onClick={() => handleCloudConnect(provider.id)}
                        className="w-full p-4 border-2 border-slate-200 rounded-lg hover:border-[var(--gold)] hover:bg-[var(--gold-pale)] transition-all text-left flex items-center gap-4"
                      >
                        <div className={`w-12 h-12 ${provider.color} rounded-lg flex items-center justify-center text-xl`}>
                          {provider.icon}
                        </div>
                        <div className="flex-1">
                          <h4 className="text-slate-900">{provider.name}</h4>
                          <p className="text-slate-600 text-sm">Connect and import documents in bulk</p>
                        </div>
                        <div className="text-[var(--gold)]">→</div>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <Alert className="bg-[var(--gold-pale)] border-[rgba(201,168,76,0.3)]">
                    <CheckCircle className="h-4 w-4 text-[var(--royal)]" />
                    <AlertDescription className="text-[var(--navy)]">
                      <strong>Connected to {cloudProviders.find(p => p.id === selectedCloud)?.name}</strong>
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2">
                    <Label htmlFor="bulk-count">Number of Documents to Import</Label>
                    <Input
                      id="bulk-count"
                      type="number"
                      min="1"
                      max="1000"
                      placeholder="e.g., 100"
                      value={bulkImportCount}
                      onChange={(e) => setBulkImportCount(e.target.value)}
                    />
                    <p className="text-slate-500 text-sm">
                      Specify how many innovation documents you want to import (up to 1000 documents)
                    </p>
                  </div>

                  <Alert>
                    <AlertDescription>
                      The system will extract metadata from each document and prepare them for Intellectual Property assessment. All documents will be queued for review by Independent Reviewers.
                    </AlertDescription>
                  </Alert>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setSelectedCloud(null);
                        setBulkImportCount('');
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={handleBulkImport}
                      disabled={!bulkImportCount || parseInt(bulkImportCount) === 0}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Import {bulkImportCount} Document{bulkImportCount !== '1' ? 's' : ''}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
